/**
 * useSelectionHandler Hook
 *
 * Manages selection state and logic for the timeslot grid.
 * Handles both single-click selection and drag selection operations.
 *
 * @requirements 2.1, 2.2, 2.3, 2.6 - Single Slot Selection
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { TimeSlot } from '../types'

/**
 * Props for the useSelectionHandler hook
 */
export interface UseSelectionHandlerProps {
  /** Array of timeslots to manage selection for */
  timeSlots: TimeSlot[]
  /** Callback fired when selection changes */
  onSelectionChange: (selectedSlots: TimeSlot[]) => void
  /** Callback fired when a booked slot is clicked (to open appointment details) */
  onBookedSlotClick?: (patientId: string, appointmentId?: string) => void
}

/**
 * Return type for the useSelectionHandler hook
 */
export interface UseSelectionHandlerReturn {
  /** Set of currently selected slot IDs */
  selectedSlotIds: Set<string>
  /** Set of slot IDs highlighted during drag operation */
  highlightedSlotIds: Set<string>
  /** Handler for slot click events */
  handleSlotClick: (slot: TimeSlot, event: React.MouseEvent) => void
  /** Handler for drag selection change (during drag) */
  handleDragSelectionChange: (elements: Element[]) => void
  /** Handler for drag selection end */
  handleDragSelectionEnd: (elements: Element[]) => void
  /** Clears all selection state */
  clearSelection: () => void
  /** Handler for container click to clear selection when clicking outside slots */
  handleContainerClick: (event: React.MouseEvent) => void
  /**
   * Validates the current selection against the provided timeslots.
   * Removes any selected/highlighted slot IDs that no longer exist in the timeslots array.
   *
   * @requirement 7.1 - Validate and update selection state when timeslot data changes
   */
  validateSelection: (currentTimeSlots: TimeSlot[]) => void
}

/**
 * Extracts slot IDs from DOM elements using the data-slot-id attribute.
 * This is a pure helper function used by drag selection handlers.
 *
 * @param elements - Array of DOM elements from drag selection
 * @returns Array of slot IDs extracted from elements
 */
export function extractSlotIdsFromElements(elements: Element[]): string[] {
  return elements
    .map((element) => element.getAttribute('data-slot-id'))
    .filter((id): id is string => id !== null)
}

/**
 * Custom hook for managing timeslot selection state.
 *
 * This hook provides:
 * - Selected slot IDs state (Set<string>) for efficient O(1) lookup
 * - Highlighted slot IDs state for drag preview
 * - Slot map for efficient slot retrieval by ID
 * - Click handlers for single selection, toggle selection, and booked slot handling
 * - Drag selection handlers for highlighting and committing selections
 *
 * @param props - Hook configuration
 * @returns Selection state and handlers
 */
export function useSelectionHandler({
  timeSlots,
  onSelectionChange,
  onBookedSlotClick,
}: UseSelectionHandlerProps): UseSelectionHandlerReturn {
  // Selection state using Set<string> for O(1) lookup performance
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set())
  const [highlightedSlotIds, setHighlightedSlotIds] = useState<Set<string>>(new Set())

  // Track if we just finished a drag operation to prevent container click from clearing selection
  const justFinishedDragRef = useRef(false)

  /**
   * Create lookup map for efficient slot retrieval by ID.
   * Memoized to prevent recreation on every render.
   */
  const slotMap = useMemo(
    () => new Map(timeSlots.map((slot) => [slot.id, slot])),
    [timeSlots]
  )

  /**
   * Handler for slot click events.
   * Implements click selection logic per Requirements 2.1, 2.2, 2.3, 2.4.
   *
   * - Regular click on open/blocked slot: Select that single slot (replace existing selection)
   * - Ctrl/Cmd+click on open/blocked slot: Toggle that slot's selection without affecting others
   * - Click on already-selected slot: Deselect it
   * - Click on booked slot: Trigger onBookedSlotClick callback (no selection change)
   */
  const handleSlotClick = useCallback(
    (slot: TimeSlot, event: React.MouseEvent) => {
      // Handle booked slots - open appointment details via callback
      // Requirement 2.4: Clicking booked slot opens Appointment_Details_Sidebar
      if (slot.status === 'booked') {
        if (onBookedSlotClick && slot.patientId) {
          onBookedSlotClick(slot.patientId, slot.appointmentId)
        }
        return
      }

      // Open and blocked slots can be selected (blocked for unblocking)
      // Handle Ctrl/Cmd click for toggle selection
      // Requirement 2.3: Ctrl/Cmd+click toggles slot selection without affecting others
      if (event.ctrlKey || event.metaKey) {
        setSelectedSlotIds((prev) => {
          const next = new Set(prev)
          if (next.has(slot.id)) {
            // Slot is already selected - deselect it
            next.delete(slot.id)
          } else {
            // Slot is not selected - add it to selection
            next.add(slot.id)
          }
          return next
        })
      } else {
        // Regular click - check if clicking on already selected slot
        // Requirement 2.2: Clicking already selected slot deselects it
        if (selectedSlotIds.has(slot.id) && selectedSlotIds.size === 1) {
          // Only this slot is selected, clicking it again deselects
          setSelectedSlotIds(new Set())
        } else {
          // Requirement 2.1: Regular click selects single slot (replaces selection)
          setSelectedSlotIds(new Set([slot.id]))
        }
      }
    },
    [onBookedSlotClick, selectedSlotIds]
  )

  /**
   * Handler for drag selection change (called during drag).
   * Extracts slot IDs from selected DOM elements, filters to only include
   * open and blocked slots, and updates highlightedSlotIds state.
   *
   * Requirement 3.2: While dragging, highlight all "Open" and "Blocked" timeslots that
   * intersect with the Selection_Box (ignoring "Booked" slots).
   */
  const handleDragSelectionChange = useCallback(
    (elements: Element[]) => {
      // Extract slot IDs from DOM elements using data-slot-id attribute
      const slotIds = extractSlotIdsFromElements(elements)

      // Filter to only include slots with status 'open' or 'blocked'
      // Booked slots should not be highlighted during drag
      const selectableSlotIds = slotIds.filter((id) => {
        const slot = slotMap.get(id)
        return slot && (slot.status === 'open' || slot.status === 'blocked')
      })

      // Update highlighted slots state
      setHighlightedSlotIds(new Set(selectableSlotIds))
    },
    [slotMap]
  )

  /**
   * Ref to track the last known modifier key state (Ctrl/Cmd).
   * This is needed because the @air/react-drag-to-select library's
   * onSelectionEnd callback does not pass keyboard event state.
   * We track it via keydown/keyup listeners on the window.
   */
  const isAdditiveRef = useRef(false)

  /**
   * Effect to track modifier key state (Ctrl/Cmd) via keydown/keyup events.
   * Updates isAdditiveRef so handleDragSelectionEnd can determine whether
   * to replace or add to the existing selection.
   *
   * Requirement 3.5: Ctrl/Cmd during drag adds newly selected slots to existing selection.
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        isAdditiveRef.current = true
      }
    }
    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        isAdditiveRef.current = false
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  /**
   * Handler for drag selection end.
   * Commits highlighted slots to the selection state.
   *
   * Requirement 3.3: When drag ends, select all highlighted timeslots.
   * Requirement 3.5: When Ctrl/Cmd is held during drag, add newly selected
   * slots to the existing selection (union). Otherwise, replace the selection.
   *
   * After committing, clears highlightedSlotIds.
   */
  const handleDragSelectionEnd = useCallback(
    (elements: Element[]) => {
      // Mark that we just finished a drag to prevent container click from clearing
      justFinishedDragRef.current = true
      // Reset the flag after a short delay (after click event would have fired)
      setTimeout(() => {
        justFinishedDragRef.current = false
      }, 100)

      // Extract slot IDs from DOM elements using data-slot-id attribute
      const slotIds = extractSlotIdsFromElements(elements)

      // Filter to only include slots with status 'open' or 'blocked'
      // Booked slots should not be selected
      const selectableSlotIds = slotIds.filter((id) => {
        const slot = slotMap.get(id)
        return slot && (slot.status === 'open' || slot.status === 'blocked')
      })

      // Only update selection if we actually selected something
      if (selectableSlotIds.length > 0) {
        // Commit highlighted slots to selection
        setSelectedSlotIds((prev) => {
          if (isAdditiveRef.current) {
            // Ctrl/Cmd was held - add to existing selection (union)
            const next = new Set(prev)
            selectableSlotIds.forEach((id) => next.add(id))
            return next
          }
          // No modifier key - replace selection with drag-selected slots
          return new Set(selectableSlotIds)
        })
      }

      // Clear highlighted slots after commit
      setHighlightedSlotIds(new Set())
    },
    [slotMap]
  )

  /**
   * Validates the current selection against the provided timeslots.
   * Removes any selected/highlighted slot IDs that no longer exist in the timeslots array.
   *
   * @requirement 7.1 - Validate and update selection state when timeslot data changes
   */
  const validateSelection = useCallback((currentTimeSlots: TimeSlot[]) => {
    const validIds = new Set(currentTimeSlots.map(s => s.id))

    setSelectedSlotIds(prev => {
      const filtered = new Set(Array.from(prev).filter(id => validIds.has(id)))
      // Only update if something was removed
      if (filtered.size === prev.size) return prev
      return filtered
    })

    setHighlightedSlotIds(prev => {
      const filtered = new Set(Array.from(prev).filter(id => validIds.has(id)))
      if (filtered.size === prev.size) return prev
      return filtered
    })
  }, [])

  /**
   * Clears all selection and highlight state.
   */
  const clearSelection = useCallback(() => {
    setSelectedSlotIds(new Set())
    setHighlightedSlotIds(new Set())
  }, [])

  /**
   * Handler for container click events.
   * Clears selection when clicking outside all timeslot elements.
   * Uses data-slot-id attribute to identify slot elements.
   *
   * Requirement 2.6: Clicking outside all timeslots clears the current selection.
   */
  const handleContainerClick = useCallback(
    (event: React.MouseEvent) => {
      // Don't clear selection if we just finished a drag operation
      if (justFinishedDragRef.current) {
        return
      }

      // Check if click was on a timeslot element by looking for data-slot-id attribute
      const target = event.target as HTMLElement
      if (!target.closest('[data-slot-id]')) {
        clearSelection()
      }
    },
    [clearSelection]
  )

  /**
   * Notify parent of selection changes.
   * Converts selected IDs to full TimeSlot objects using the slotMap.
   */
  useEffect(() => {
    const selectedSlots = Array.from(selectedSlotIds)
      .map((id) => slotMap.get(id))
      .filter((slot): slot is TimeSlot => slot !== undefined)
    onSelectionChange(selectedSlots)
  }, [selectedSlotIds, slotMap, onSelectionChange])

  /**
   * Cancel drag operation when window loses focus.
   * Clears highlighted slots to reset any in-progress drag selection.
   *
   * @requirement 8.4 - Cancel drag operation and reset state when window loses focus
   */
  useEffect(() => {
    const handleBlur = () => {
      setHighlightedSlotIds(new Set())
    }

    window.addEventListener('blur', handleBlur)
    return () => window.removeEventListener('blur', handleBlur)
  }, [])

  return {
    selectedSlotIds,
    highlightedSlotIds,
    handleSlotClick,
    handleDragSelectionChange,
    handleDragSelectionEnd,
    clearSelection,
    handleContainerClick,
    validateSelection,
  }
}
